import React from 'react';
import { 
  Box, 
  Container, 
  Grid, 
  Typography, 
  Button, 
  List, 
  ListItem, 
  ListItemIcon, 
  ListItemText, 
  Paper 
} from '@mui/material';
import { 
  CheckCircleOutline, 
  LocalCafe, 
  Inventory, 
  Loyalty, 
  LocalOffer, 
  QrCodeScanner 
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import PublicLayout from "../../layouts/PublicLayout";

export default function CafeShops() {
  return (
    <PublicLayout>
      <Box sx={{ bgcolor: '#fff', py: { xs: 8, md: 10 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={8} alignItems="flex-start">
            
            {/* LEFT CONTENT - 60% approx */}
            <Grid item xs={12} md={7}>
              <Typography variant="overline" sx={{ color: '#f6521a', fontWeight: 800, letterSpacing: 1.5 }}>
                TPOS FOR CAFES & COFFEE SHOPS
              </Typography>
              
              <Typography variant="h2" sx={{ fontWeight: 600, mt: 1, mb: 2, fontSize: { xs: '1.5rem', md: '2.5rem' }, lineHeight: 1.1 }}>
                Brew the Perfect Customer Experience with <span style={{ color: "#f6521a" }}>Lightning-Fast Billing.</span>
              </Typography>

              <Typography variant="h6" sx={{ color: 'text.secondary', fontWeight: 500, mb: 4, lineHeight: 1.5 }}>
                From complex custom orders to managing perishable milk inventory, TrueMenu gives your cafe the technology to scale effortlessly.
              </Typography>

              <Typography variant="body1" sx={{ color: 'text.primary', mb: 5, fontSize: '1.1rem', lineHeight: 1.8 }}>
                A successful cafe thrives on speed, personalization, and customer loyalty. When a customer asks for a "Venti Iced Hazelnut Latte with Soy Milk, half-sweet, and an extra shot," your barista shouldn't have to hunt for buttons. 
                <strong> TPOS is engineered for the fast-paced coffee shop environment.</strong> We provide an intuitive, touch-optimized interface that handles complex modifiers in seconds, alongside robust inventory tracking that measures your coffee beans down to the gram.
              </Typography>

              <Typography variant="h5" sx={{ fontWeight: 700, mb: 3 }}>
                Specialized TPOS Features:
              </Typography>

              <Grid container spacing={3}>
                {[
                  {
                    title: "Fast-Tap Modifier Matrix",
                    desc: "An intelligent, single-screen modifier system. Baristas can add 'Extra Shot' or 'Almond Milk' with a single tap, updating the price and KOT instantly.",
                    icon: <LocalCafe sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Micro-Ingredient Recipe Management", 
                    desc: "Link every menu item to a precise recipe. Sell a Cappuccino, and TPOS automatically deducts 18g of coffee beans and 150ml of milk.",
                    icon: <Inventory sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Integrated Loyalty & CRM", 
                    desc: "Turn walk-ins into regulars. Capture customer numbers via digital receipts and automatically assign and redeem loyalty points.",
                    icon: <Loyalty sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "Dynamic Combo & Happy Hour Pricing", 
                    desc: "Easily set up 'Coffee + Croissant' combos or schedule automated 'Happy Hour' discounts that activate without manual intervention.",
                    icon: <LocalOffer sx={{ color: '#f6521a' }} />
                  },
                  { 
                    title: "QR Table Ordering for Dine-In", 
                    desc: "Let customers scan a QR code at their table, and order their second round of coffees and desserts directly from their phones.",
                    icon: <QrCodeScanner sx={{ color: '#f6521a' }} />
                  }
                ].map((item, index) => (
                  <Grid item xs={12} sm={6} key={index}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, mb: 2 }}>
                      <Box sx={{ mt: 0.5 }}>{item.icon}</Box>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>{item.title}</Typography>
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{item.desc}</Typography>
                      </Box>
                    </Box>
                  </Grid>
                ))}
              </Grid>

            </Grid>

            {/* RIGHT IMAGE - 40% approx */}
            <Grid item xs={12} md={5} sx={{ position: 'sticky', top: 100 }}>
              <Paper 
                elevation={0} 
                sx={{ 
                  borderRadius: 4, 
                  overflow: 'hidden', 
                  background: 'linear-gradient(145deg, #ffffff 0%, #f9f9f9 100%)',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  height: '100%'
                }}
              >
                <Box
                  sx={{
                    width: '100%',
                    height: { xs: 300, md: 550 },
                    borderRadius: 3,
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'right',
                    float:'right'                    
                  }}
                >
                  <img 
                    src="/img/cafe.png" 
                    alt="Cafe Management POS" 
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                </Box>
                
                <Box sx={{ p: 3, width: '100%' }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>Business Impact</Typography>
                  <List dense>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 35 }}><CheckCircleOutline sx={{ color: '#00ab55', fontSize: 20 }} /></ListItemIcon>
                      <ListItemText primary="Reduce order-taking time to under 15 seconds, keeping the morning rush moving." />
                    </ListItem>
                    <ListItem disableGutters>
                      <ListItemIcon sx={{ minWidth: 35 }}><CheckCircleOutline sx={{ color: '#00ab55', fontSize: 20 }} /></ListItemIcon>
                      <ListItemText primary="Cut inventory wastage by up to 20% through precise recipe tracking and alerts." />
                    </ListItem>
                  </List>
                </Box>
              </Paper>
            </Grid>

          </Grid>
        </Container>
      </Box>
    </PublicLayout>
  );
}